import {Component, DestroyRef, inject} from '@angular/core';
import {Subscription, timer} from 'rxjs';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormField, MatLabel, MatError} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslocoModule, TranslocoService} from '@jsverse/transloco';
import {SignupService, EligibleTerm} from './signup.service';
import {TermFullTextDialogComponent} from './term-full-text-dialog.component';
import {TenantConfigService} from '../../services/tenant-config.service';
import {TenantI18nService} from '../../services/tenant-i18n.service';
import {CaptchaConfig, CaptchaService, CaptchaToken} from '../../services/captcha.service';
import {LanguageService} from '../../../../../shared/i18n/language.service';

const ERROR_KEYS: Record<string, string> = {
  signup_disabled: 'signup.errors.signupDisabled',
  // email_taken was REMOVED by Story 45.1 (EO-268). /register no longer emits it
  // — a duplicate address returns the same neutral 201 as a fresh registration,
  // so there is no such error to map. Re-adding this key would be harmless on its
  // own but signals the branch is back; the spec asserts it stays gone.
  invalid_email: 'common.errors.emailInvalid',
  weak_password: 'common.errors.passwordTooShort',
  invalid_request: 'common.errors.invalidRequest',
  captcha_failed: 'common.errors.captchaFailed',
  server_error: 'common.errors.serverError',
};

const CONFIG_SPINNER_TIMEOUT_MS = 2000;

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField, MatLabel, MatError, MatInput,
    MatButton, MatIconButton, MatIcon,
    MatCard, MatCardContent,
    MatCheckbox,
    RouterLink,
    TranslocoModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.css'
})
export class SignupComponent {
  signupForm: FormGroup;
  error: string | null = null;
  loading = false;
  hidePassword = true;
  configLoading = false;
  // Story 45.1 (EO-268) — signup terminates in a check-your-email state instead
  // of sending the user to the login page. Mirrors ForgotPasswordComponent's
  // `submitted` flag, the in-repo pattern for an anti-enumeration terminal
  // state: same component, no new route (so Epic 30's serve-pages interception
  // is untouched), same confirmation for every outcome.
  submitted = false;

  eligibleTerms: EligibleTerm[] = [];
  termsLoading = false;
  termsLoadError = false;
  readonly termDecisions = new Map<string, 'accepted' | 'declined'>();

  private configTimeoutSub?: Subscription;
  private clientId = '';
  private captchaRequired = false;
  // Story 54.4 — which captcha discovery says to run; captchaSolving drives
  // aria-busy + the polite status line while the proof-of-work runs.
  private captchaConfig: CaptchaConfig | undefined;
  captchaSolving = false;

  private readonly signupService = inject(SignupService);
  private readonly tenantConfigService = inject(TenantConfigService);
  private readonly tenantI18nService = inject(TenantI18nService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly captchaService = inject(CaptchaService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly dialog = inject(MatDialog);

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe(params => {
      this.clientId = params['client_id'] || '';
      if (this.clientId) {
        this.configLoading = true;
        this.configTimeoutSub = timer(CONFIG_SPINNER_TIMEOUT_MS)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.configLoading = false);
        this.tenantConfigService.fetchConfig(this.clientId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: config => {
            this.captchaRequired = config.captcha_required === true;
            this.captchaConfig = config.captcha;
            this.tenantI18nService.apply(config);
            this.languageService.applyEnvironmentDefault(config.default_language, Object.keys(config.i18n_overrides ?? {}));
            this.configTimeoutSub?.unsubscribe();
            this.configLoading = false;
          },
          error: () => {
            this.captchaRequired = false;
            this.configTimeoutSub?.unsubscribe();
            this.configLoading = false;
          },
        });
        this.loadEligibleTerms(this.clientId);
      }
    });
    this.signupForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required, Validators.minLength(12)]),
    });
  }

  get hasUnacceptedMandatoryTerm(): boolean {
    return this.eligibleTerms
      .filter(t => t.kind === 'term')
      .some(t => this.termDecisions.get(t.id) !== 'accepted');
  }

  toggleTerm(termId: string, accepted: boolean): void {
    this.termDecisions.set(termId, accepted ? 'accepted' : 'declined');
  }

  isTermAccepted(termId: string): boolean {
    return this.termDecisions.get(termId) === 'accepted';
  }

  openFullText(term: EligibleTerm): void {
    this.dialog.open(TermFullTextDialogComponent, {
      data: {title: term.resume, fullText: term.fullText},
      maxWidth: '680px',
      width: '90vw',
    });
  }

  async submit(): Promise<void> {
    if (this.signupForm.invalid || this.loading || this.termsLoading || this.termsLoadError) return;
    if (this.hasUnacceptedMandatoryTerm) return;
    if (!this.clientId) {
      this.error = this.translocoService.translate('signup.errors.invalidLink');
      return;
    }
    this.loading = true;
    this.error = null;

    let captcha: CaptchaToken | undefined;
    if (this.captchaRequired) {
      // Story 54.4 — solved here, at submit, every time (never at load).
      this.captchaSolving = true;
      try {
        captcha = await this.captchaService.solve('register', this.clientId, this.captchaConfig);
      } catch {
        this.loading = false;
        this.error = this.translocoService.translate(ERROR_KEYS['captcha_failed']);
        return;
      } finally {
        this.captchaSolving = false;
      }
    }

    const termDecisions = this.eligibleTerms.map(t => ({
      termId: t.id,
      decision: this.termDecisions.get(t.id) ?? 'declined',
    }));

    this.signupService.register({
      client_id: this.clientId,
      email: this.signupForm.get('email')!.value.trim(),
      password: this.signupForm.get('password')!.value,
      termDecisions,
      // The selected provider's field only (AC6).
      ...(captcha ? {[captcha.field]: captcha.value} : {}),
    }).subscribe({
      next: () => {
        this.loading = false;
        // Terminal state, not a redirect to sign-in. The response is neutral by
        // design, so the UI cannot know (and must not imply) whether an account
        // was created — every outcome shows the same confirmation. The
        // "back to sign in" link in that state is what a verification-optional
        // user follows to sign in immediately; a verification-required user
        // follows it and is told to verify, exactly as before this story.
        this.submitted = true;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        const code = err.error?.error || 'server_error';
        this.error = this.translocoService.translate(ERROR_KEYS[code] ?? ERROR_KEYS['server_error']);
      }
    });
  }

  private loadEligibleTerms(clientId: string): void {
    this.termsLoading = true;
    this.termsLoadError = false;
    this.signupService.getEligibleTerms(clientId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: terms => {
        this.eligibleTerms = terms;
        this.termsLoading = false;
      },
      error: () => {
        this.termsLoadError = true;
        this.termsLoading = false;
      },
    });
  }
}
